import React, { useState, useEffect } from 'react';
import { Settings as SettingsIcon, User, Bell, Shield, Palette, Globe, Download, Trash2, Save, Edit, Eye, EyeOff, Check, AlertCircle, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useNavigate, useOutletContext } from 'react-router-dom';
import axiosInstance from '../services/axiosInstance';

const Settings = () => {
  const navigate = useNavigate();
  const { user } = useOutletContext();
  const [activeTab, setActiveTab] = useState('profile');
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    location: '',
    // New stock market related fields
    tradingExperience: '',
    investmentGoals: '',
    riskTolerance: '',
    preferredSectors: '',
    investmentHorizon: '',
    monthlyInvestment: '',
    // New general fields
    dateOfBirth: '',
    gender: '',
    occupation: '',
    education: '',
    annualIncome: '',
    emergencyContact: ''
  });

  // Password change states
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [passwordChangeData, setPasswordChangeData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [passwordChangeLoading, setPasswordChangeLoading] = useState(false);
  const [passwordChangeMessage, setPasswordChangeMessage] = useState("");
  const [passwordChangeError, setPasswordChangeError] = useState("");
  
  // OTP states for password change
  const [showPasswordOtpModal, setShowPasswordOtpModal] = useState(false);
  const [passwordOtp, setPasswordOtp] = useState(["", "", "", "", "", ""]);
  const [passwordOtpVerified, setPasswordOtpVerified] = useState(false);
  const [passwordOtpLoading, setPasswordOtpLoading] = useState(false);

  // Password strength validation
  const [passwordStrength, setPasswordStrength] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    symbol: false
  });

  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: true,
    priceAlerts: true,
    portfolioUpdates: true,
    marketNews: false,
    weeklyReports: true,
    securityAlerts: true
  });

  const [securitySettings, setSecuritySettings] = useState({
    twoFactorAuth: false,
    loginNotifications: true,
    sessionTimeout: '24h',
    passwordExpiry: '90d'
  });

  const [preferences, setPreferences] = useState({
    theme: 'light',
    language: 'en',
    currency: 'INR',
    timezone: 'Asia/Kolkata',
    dateFormat: 'DD/MM/YYYY',
    decimalPlaces: 2
  });

  // Update profile data when user context is available
  useEffect(() => {
    if (user) {
      console.log('User context available:', user); // Debug log
      setProfileData({
        name: (user.name || '').toString(),
        email: (user.email || '').toString(),
        phone: (user.phone || '').toString(),
        company: (user.company || '').toString(),
        location: (user.location || '').toString(),
        // New stock market related fields
        tradingExperience: (user.tradingExperience || '').toString(),
        investmentGoals: (user.investmentGoals || '').toString(),
        riskTolerance: (user.riskTolerance || '').toString(),
        preferredSectors: (user.preferredSectors || '').toString(),
        investmentHorizon: (user.investmentHorizon || '').toString(),
        monthlyInvestment: (user.monthlyInvestment || '').toString(),
        // New general fields
        dateOfBirth: (user.dateOfBirth || '').toString(),
        gender: (user.gender || '').toString(),
        occupation: (user.occupation || '').toString(),
        education: (user.education || '').toString(),
        annualIncome: (user.annualIncome || '').toString(),
        emergencyContact: (user.emergencyContact || '').toString()
      });
      setLoading(false);
    }
  }, [user]);

  // Fallback: fetch user profile from backend if context is not available
  useEffect(() => {
    if (!user && !loading) {
      fetchUserProfile();
    }
  }, [user, loading]);

  // Fetch security settings when component loads
  useEffect(() => {
    fetchSecuritySettings();
  }, []);

  // Debug: Log security settings changes
  useEffect(() => {
    console.log('🔐 [SECURITY] Security settings state changed:', securitySettings);
  }, [securitySettings]);

  const fetchSecuritySettings = async () => {
    try {
      console.log('🔍 [SECURITY] Starting to fetch security settings...');
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('❌ [SECURITY] No token found for fetching security settings');
        return;
      }

      console.log('✅ [SECURITY] Token found, making API call to /auth/security/settings...');
      const response = await axiosInstance.get('/auth/security/settings');
      
      console.log('📡 [SECURITY] Raw API response:', response);
      console.log('📊 [SECURITY] Response data:', response.data);

      if (response.data.success) {
        const data = response.data.data;
        console.log('✅ [SECURITY] Security settings fetched successfully:', data);
        
        const newSettings = {
          twoFactorAuth: data.twoFactorAuth || false,
          loginNotifications: data.loginNotifications || true,
          sessionTimeout: data.sessionTimeout || '24h',
          passwordExpiry: data.passwordExpiry || '90d'
        };
        
        console.log('🔄 [SECURITY] Setting new security settings state:', newSettings);
        setSecuritySettings(newSettings);
      } else {
        console.error('❌ [SECURITY] API returned success: false:', response.data.message);
      }
    } catch (error) {
      console.error('💥 [SECURITY] Error fetching security settings:', error);
      console.error('💥 [SECURITY] Error details:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data
      });
      // Use default values if fetch fails
      console.log('🔄 [SECURITY] Using default security settings due to fetch error');
    }
  };

  const fetchUserProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      console.log('Fetching profile from backend...'); // Debug log
      const response = await axiosInstance.get('/auth/profile');

      if (response.data.success) {
        const data = response.data;
        console.log('Profile API response:', data); // Debug log
        if (data.success) {
          setProfileData({
            name: (data.data.name || '').toString(),
            email: (data.data.email || '').toString(),
            phone: (data.data.phone || '').toString(),
            company: (data.data.company || '').toString(),
            location: (data.data.location || '').toString(),
            // New stock market related fields
            tradingExperience: (data.data.tradingExperience || '').toString(),
            investmentGoals: (data.data.investmentGoals || '').toString(),
            riskTolerance: (data.data.riskTolerance || '').toString(),
            preferredSectors: (data.data.preferredSectors || '').toString(),
            investmentHorizon: (data.data.investmentHorizon || '').toString(),
            monthlyInvestment: (data.data.monthlyInvestment || '').toString(),
            // New general fields
            dateOfBirth: (data.data.dateOfBirth || '').toString(),
            gender: (data.data.gender || '').toString(),
            occupation: (data.data.occupation || '').toString(),
            education: (data.data.education || '').toString(),
            annualIncome: (data.data.annualIncome || '').toString(),
            emergencyContact: (data.data.emergencyContact || '').toString()
          });
        }
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  // Validation function for enum fields
  const validateEnumField = (value, validValues) => {
    return value && validValues.includes(value) ? value : null;
  };

  const handleProfileSave = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      
      if (!token) {
        alert('No authentication token found. Please log in again.');
        navigate('/login');
        return;
      }

      const formData = new FormData();
      
      // Only append fields that have actual values
      if (profileData.name && profileData.name.trim()) {
        formData.append('name', profileData.name.trim());
      }
      if (profileData.phone && profileData.phone.trim()) {
        formData.append('phone', profileData.phone.trim());
      }
      if (profileData.company && profileData.company.trim()) {
        formData.append('company', profileData.company.trim());
      }
      if (profileData.location && profileData.location.trim()) {
        formData.append('location', profileData.location.trim());
      }
      
      // New stock market related fields - only if they have valid values
      const tradingExp = validateEnumField(profileData.tradingExperience, ['beginner', 'intermediate', 'advanced', 'expert']);
      if (tradingExp) {
        formData.append('tradingExperience', tradingExp);
      }
      
      const investmentGoals = validateEnumField(profileData.investmentGoals, ['wealth-building', 'retirement', 'income-generation', 'tax-saving', 'short-term']);
      if (investmentGoals) {
        formData.append('investmentGoals', investmentGoals);
      }
      
      const riskTolerance = validateEnumField(profileData.riskTolerance, ['conservative', 'moderate', 'aggressive', 'very-aggressive']);
      if (riskTolerance) {
        formData.append('riskTolerance', riskTolerance);
      }
      
      if (profileData.preferredSectors && profileData.preferredSectors.trim()) {
        formData.append('preferredSectors', profileData.preferredSectors.trim());
      }
      
      const investmentHorizon = validateEnumField(profileData.investmentHorizon, ['short-term', 'medium-term', 'long-term']);
      if (investmentHorizon) {
        formData.append('investmentHorizon', investmentHorizon);
      }
      
      const monthlyInvestment = validateEnumField(profileData.monthlyInvestment, ['0-5K', '5K-15K', '15K-50K', '50K-1L', '1L+']);
      if (monthlyInvestment) {
        formData.append('monthlyInvestment', monthlyInvestment);
      }
      
      // New general fields - only if they have valid values
      if (profileData.dateOfBirth && profileData.dateOfBirth.trim()) {
        formData.append('dateOfBirth', profileData.dateOfBirth.trim());
      }
      
      const gender = validateEnumField(profileData.gender, ['male', 'female', 'other', 'prefer-not-to-say']);
      if (gender) {
        formData.append('gender', gender);
      }
      
      if (profileData.occupation && profileData.occupation.trim()) {
        formData.append('occupation', profileData.occupation.trim());
      }
      
      const education = validateEnumField(profileData.education, ['high-school', 'bachelor', 'master', 'phd', 'other']);
      if (education) {
        formData.append('education', education);
      }
      
      const annualIncome = validateEnumField(profileData.annualIncome, ['0-5L', '5L-10L', '10L-25L', '25L-50L', '50L+']);
      if (annualIncome) {
        formData.append('annualIncome', annualIncome);
      }
      
      if (profileData.emergencyContact && profileData.emergencyContact.trim()) {
        formData.append('emergencyContact', profileData.emergencyContact.trim());
      }

      console.log('Saving profile data:', profileData); // Debug log
      console.log('FormData entries:');
      for (let [key, value] of formData.entries()) {
        console.log(`${key}: ${value}`);
      }
      console.log('Token:', token ? 'Present' : 'Missing'); // Debug log

      const response = await axiosInstance.post('/auth/profile/update', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      console.log('Response data:', response.data); // Debug log
      
      if (response.data.success) {
        setIsEditing(false);
        // Show success message (you can add a toast notification here)
        alert('Profile updated successfully!');
        // Refresh user context if possible
        if (user) {
          // Update the user context with new data
          Object.assign(user, {
            name: profileData.name,
            phone: profileData.phone,
            company: profileData.company,
            location: profileData.location,
            // New stock market related fields
            tradingExperience: profileData.tradingExperience,
            investmentGoals: profileData.investmentGoals,
            riskTolerance: profileData.riskTolerance,
            preferredSectors: profileData.preferredSectors,
            investmentHorizon: profileData.investmentHorizon,
            monthlyInvestment: profileData.monthlyInvestment,
            // New general fields
            dateOfBirth: profileData.dateOfBirth,
            gender: profileData.gender,
            occupation: profileData.occupation,
            education: profileData.education,
            annualIncome: profileData.annualIncome,
            emergencyContact: profileData.emergencyContact
          });
        }
      } else {
        alert(response.data.message || 'Failed to update profile');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      const errorMessage = error.response?.data?.message || error.message || 'An error occurred while updating profile';
      alert(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleNotificationToggle = (key) => {
    setNotificationSettings(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleSecurityToggle = (key) => {
    console.log(`🔄 [SECURITY] Toggling security setting: ${key}`);
    console.log(`📊 [SECURITY] Current value: ${securitySettings[key]}`);
    
    setSecuritySettings(prev => {
      const newValue = !prev[key];
      console.log(`🔄 [SECURITY] Setting ${key} to: ${newValue}`);
      return {
        ...prev,
        [key]: newValue
      };
    });
    
    console.log(`✅ [SECURITY] Security setting ${key} toggled successfully`);
  };

  const handleSecuritySettingsSave = async () => {
    try {
      console.log('🔐 [SECURITY] Starting security settings save...');
      setSaving(true);
      const token = localStorage.getItem('token');
      
      if (!token) {
        console.error('❌ [SECURITY] No authentication token found');
        alert('No authentication token found. Please log in again.');
        navigate('/login');
        return;
      }

      console.log('✅ [SECURITY] Token found, preparing form data...');
      console.log('📊 [SECURITY] Current security settings state:', securitySettings);

      const formData = new FormData();
      formData.append('twoFactorAuth', securitySettings.twoFactorAuth ? 'true' : 'false');
      formData.append('loginNotifications', securitySettings.loginNotifications ? 'true' : 'false');
      formData.append('sessionTimeout', securitySettings.sessionTimeout);
      formData.append('passwordExpiry', securitySettings.passwordExpiry);

      console.log('📋 [SECURITY] FormData prepared:');
      for (let [key, value] of formData.entries()) {
        console.log(`  ${key}: ${value}`);
      }

      console.log('🚀 [SECURITY] Making API call to /auth/security/update...');
      
      // Try the correct endpoint first
      const response = await axiosInstance.post('/auth/security/update', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      console.log('✅ [SECURITY] API response received:', response.data);

      if (response.data.success) {
        console.log('🎉 [SECURITY] Security settings updated successfully!');
        alert('Security settings updated successfully!');
        // Refresh security settings
        await fetchSecuritySettings();
      } else {
        console.error('❌ [SECURITY] API returned success: false:', response.data.message);
        alert(response.data.message || 'Failed to update security settings');
      }
    } catch (error) {
      console.error('💥 [SECURITY] Error updating security settings:', error);
      console.error('💥 [SECURITY] Error details:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          headers: error.config?.headers
        }
      });
      
      const errorMessage = error.response?.data?.message || error.message || 'An error occurred while updating security settings';
      console.error('💥 [SECURITY] Final error message:', errorMessage);
      alert(errorMessage);
    } finally {
      console.log('🏁 [SECURITY] Security settings save operation completed');
      setSaving(false);
    }
  };

  // Password strength validation
  const checkPasswordStrength = (password) => {
    setPasswordStrength({
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /\d/.test(password),
      symbol: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)
    });
  };

  const isPasswordStrong = () => {
    return Object.values(passwordStrength).every(Boolean);
  };

  const passwordsMatch = () => {
    return passwordChangeData.newPassword === passwordChangeData.confirmPassword && passwordChangeData.newPassword !== '';
  };

  // Password change functions
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (!isPasswordStrong()) {
      setPasswordChangeError("Please ensure your password meets all strength requirements");
      return;
    }
    
    if (!passwordsMatch()) {
      setPasswordChangeError("New passwords do not match");
      return;
    }

    setPasswordChangeLoading(true);
    setPasswordChangeError("");

    try {
      // First, verify current password
      const response = await axiosInstance.post('/auth/verify-current-password', {
        currentPassword: passwordChangeData.currentPassword
      });

      if (response.data.success) {
        // Send OTP for password change
        const otpResponse = await axiosInstance.post('/auth/send-password-change-otp');
        if (otpResponse.data.success) {
          setShowPasswordOtpModal(true);
          setPasswordChangeMessage("OTP sent to your email for verification");
        } else {
          setPasswordChangeError(otpResponse.data.message || "Failed to send OTP");
        }
      } else {
        setPasswordChangeError("Current password is incorrect");
      }
    } catch (error) {
      console.error("Password change error:", error);
      const errorMessage = error.response?.data?.message || error.message || "An error occurred during password change";
      setPasswordChangeError(errorMessage);
    } finally {
      setPasswordChangeLoading(false);
    }
  };

  const handlePasswordOtpChange = (element, index) => {
    if (isNaN(element.value)) return;

    setPasswordOtp([...passwordOtp.map((d, idx) => (idx === index ? element.value : d))]);

    if (element.value && element.nextSibling) {
      element.nextSibling.focus();
    }
  };

  const handleVerifyPasswordOtp = async () => {
    setPasswordOtpLoading(true);
    setPasswordChangeError("");

    const otpString = passwordOtp.join("");
    try {
      const response = await axiosInstance.post('/auth/change-password-with-otp', {
        currentPassword: passwordChangeData.currentPassword,
        newPassword: passwordChangeData.newPassword,
        otp: otpString
      });

      if (response.data.success) {
        setPasswordOtpVerified(true);
        setPasswordChangeMessage("Password changed successfully!");
        
        // Clear form data
        setPasswordChangeData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        setPasswordOtp(["", "", "", "", "", ""]);
        
        // Close modal after 3 seconds
        setTimeout(() => {
          setShowPasswordOtpModal(false);
          setShowPasswordChange(false);
          setPasswordChangeMessage("");
          setPasswordOtpVerified(false);
        }, 3000);
      } else {
        setPasswordChangeError(response.data.message || "OTP verification failed");
      }
    } catch (error) {
      console.error("OTP verification error:", error);
      const errorMessage = error.response?.data?.message || error.message || "An error occurred during OTP verification";
      setPasswordChangeError(errorMessage);
    } finally {
      setPasswordOtpLoading(false);
    }
  };

  const clearPasswordChangeModal = () => {
    setShowPasswordChange(false);
    setPasswordChangeData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setPasswordChangeError("");
    setPasswordChangeMessage("");
    setPasswordStrength({
      length: false,
      uppercase: false,
      lowercase: false,
      number: false,
      symbol: false
    });
  };

  const PasswordRequirement = ({ met, text }) => (
    <div className={`flex items-center space-x-2 text-sm ${met ? 'text-green-400' : 'text-gray-400'}`}>
      {met ? (
        <Check size={16} className="text-green-400" />
      ) : (
        <AlertCircle size={16} className="text-gray-400" />
      )}
      <span>{text}</span>
    </div>
  );

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'security', label: 'Security', icon: Shield },
    { id: 'preferences', label: 'Preferences', icon: Palette }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  // Debug: Show current profile data
  console.log('Current profile data:', profileData);
  
  // Debug: Show current security settings
  console.log('🔐 [SECURITY] Current security settings state:', securitySettings);

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-2xl mb-6">
            <SettingsIcon className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Settings</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Manage your account settings, preferences, and security options
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="flex flex-wrap justify-center gap-2 mb-8">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <Button
                key={tab.id}
                variant={activeTab === tab.id ? "default" : "outline"}
                onClick={() => setActiveTab(tab.id)}
                className="flex items-center gap-2 px-6 py-3"
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </Button>
            );
          })}
        </div>

        {/* Profile Settings */}
        {activeTab === 'profile' && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-2xl">Profile Information</CardTitle>
                <div className="flex gap-2">
                  {isEditing ? (
                    <>
                      <Button 
                        onClick={handleProfileSave} 
                        className="bg-green-600 hover:bg-green-700"
                        disabled={saving}
                      >
                        {saving ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                            Saving...
                          </>
                        ) : (
                          <>
                            <Save className="w-4 h-4 mr-2" />
                            Save
                          </>
                        )}
                      </Button>
                      <Button 
                        variant="outline" 
                        onClick={() => {
                          setIsEditing(false);
                          // Reset to original user data
                          if (user) {
                            setProfileData({
                              name: user.name || '',
                              email: user.email || '',
                              phone: user.phone || '',
                              company: user.company || '',
                              location: user.location || '',
                              // New stock market related fields
                              tradingExperience: user.tradingExperience || '',
                              investmentGoals: user.investmentGoals || '',
                              riskTolerance: user.riskTolerance || '',
                              preferredSectors: user.preferredSectors || '',
                              investmentHorizon: user.investmentHorizon || '',
                              monthlyInvestment: user.monthlyInvestment || '',
                              // New general fields
                              dateOfBirth: user.dateOfBirth || '',
                              gender: user.gender || '',
                              occupation: user.occupation || '',
                              education: user.education || '',
                              annualIncome: user.annualIncome || '',
                              emergencyContact: user.emergencyContact || ''
                            });
                          }
                        }}
                        disabled={saving}
                      >
                        Cancel
                      </Button>
                    </>
                  ) : (
                    <Button onClick={() => setIsEditing(true)}>
                      <Edit className="w-4 h-4 mr-2" />
                      Edit
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-8">
              {/* Basic Information */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      value={profileData.name}
                      onChange={(e) => setProfileData({...profileData, name: e.target.value})}
                      disabled={!isEditing}
                      className="mt-2"
                      placeholder="Enter your full name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={profileData.email}
                      disabled={true} // Email should not be editable
                      className="mt-2 bg-gray-100"
                      placeholder="Your email address"
                    />
                    <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone Number *</Label>
                    <Input
                      id="phone"
                      value={profileData.phone}
                      onChange={(e) => setProfileData({...profileData, phone: e.target.value})}
                      disabled={!isEditing}
                      className="mt-2"
                      placeholder="Enter your phone number"
                    />
                  </div>
                  <div>
                    <Label htmlFor="dateOfBirth">Date of Birth</Label>
                    <Input
                      id="dateOfBirth"
                      type="date"
                      value={profileData.dateOfBirth}
                      onChange={(e) => setProfileData({...profileData, dateOfBirth: e.target.value})}
                      disabled={!isEditing}
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label htmlFor="gender">Gender</Label>
                    <Select value={profileData.gender} onValueChange={(value) => setProfileData({...profileData, gender: value})} disabled={!isEditing}>
                      <SelectTrigger className="mt-2">
                        <SelectValue placeholder="Select gender" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="male">Male</SelectItem>
                        <SelectItem value="female">Female</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                        <SelectItem value="prefer-not-to-say">Prefer not to say</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="emergencyContact">Emergency Contact</Label>
                    <Input
                      id="emergencyContact"
                      value={profileData.emergencyContact}
                      onChange={(e) => setProfileData({...profileData, emergencyContact: e.target.value})}
                      disabled={!isEditing}
                      className="mt-2"
                      placeholder="Emergency contact number"
                    />
                  </div>
                </div>
              </div>

              {/* Professional Information */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Professional Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="company">Company</Label>
                    <Input
                      id="company"
                      value={profileData.company}
                      onChange={(e) => setProfileData({...profileData, company: e.target.value})}
                      disabled={!isEditing}
                      className="mt-2"
                      placeholder="Enter your company name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="occupation">Occupation</Label>
                    <Input
                      id="occupation"
                      value={profileData.occupation}
                      onChange={(e) => setProfileData({...profileData, occupation: e.target.value})}
                      disabled={!isEditing}
                      className="mt-2"
                      placeholder="Your job title/occupation"
                    />
                  </div>
                  <div>
                    <Label htmlFor="education">Education</Label>
                    <Select value={profileData.education} onValueChange={(value) => setProfileData({...profileData, education: value})} disabled={!isEditing}>
                      <SelectTrigger className="mt-2">
                        <SelectValue placeholder="Select education level" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="high-school">High School</SelectItem>
                        <SelectItem value="bachelor">Bachelor's Degree</SelectItem>
                        <SelectItem value="master">Master's Degree</SelectItem>
                        <SelectItem value="phd">PhD</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="annualIncome">Annual Income</Label>
                    <Select value={profileData.annualIncome} onValueChange={(value) => setProfileData({...profileData, annualIncome: value})} disabled={!isEditing}>
                      <SelectTrigger className="mt-2">
                        <SelectValue placeholder="Select income range" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0-5L">₹0 - ₹5 Lakhs</SelectItem>
                        <SelectItem value="5L-10L">₹5 - ₹10 Lakhs</SelectItem>
                        <SelectItem value="10L-25L">₹10 - ₹25 Lakhs</SelectItem>
                        <SelectItem value="25L-50L">₹25 - ₹50 Lakhs</SelectItem>
                        <SelectItem value="50L+">₹50+ Lakhs</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="location">Location</Label>
                    <Input
                      id="location"
                      value={profileData.location}
                      onChange={(e) => setProfileData({...profileData, location: e.target.value})}
                      disabled={!isEditing}
                      className="mt-2"
                      placeholder="Enter your location"
                    />
                  </div>
                </div>
              </div>

              {/* Stock Market & Investment Profile */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Investment Profile</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="tradingExperience">Trading Experience</Label>
                    <Select value={profileData.tradingExperience} onValueChange={(value) => setProfileData({...profileData, tradingExperience: value})} disabled={!isEditing}>
                      <SelectTrigger className="mt-2">
                        <SelectValue placeholder="Select experience level" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="beginner">Beginner (0-1 years)</SelectItem>
                        <SelectItem value="intermediate">Intermediate (1-3 years)</SelectItem>
                        <SelectItem value="advanced">Advanced (3-5 years)</SelectItem>
                        <SelectItem value="expert">Expert (5+ years)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="investmentGoals">Investment Goals</Label>
                    <Select value={profileData.investmentGoals} onValueChange={(value) => setProfileData({...profileData, investmentGoals: value})} disabled={!isEditing}>
                      <SelectTrigger className="mt-2">
                        <SelectValue placeholder="Select primary goal" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="wealth-building">Wealth Building</SelectItem>
                        <SelectItem value="retirement">Retirement Planning</SelectItem>
                        <SelectItem value="income-generation">Income Generation</SelectItem>
                        <SelectItem value="tax-saving">Tax Saving</SelectItem>
                        <SelectItem value="short-term">Short-term Gains</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="riskTolerance">Risk Tolerance</Label>
                    <Select value={profileData.riskTolerance} onValueChange={(value) => setProfileData({...profileData, riskTolerance: value})} disabled={!isEditing}>
                      <SelectTrigger className="mt-2">
                        <SelectValue placeholder="Select risk level" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="conservative">Conservative</SelectItem>
                        <SelectItem value="moderate">Moderate</SelectItem>
                        <SelectItem value="aggressive">Aggressive</SelectItem>
                        <SelectItem value="very-aggressive">Very Aggressive</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="investmentHorizon">Investment Horizon</Label>
                    <Select value={profileData.investmentHorizon} onValueChange={(value) => setProfileData({...profileData, investmentHorizon: value})} disabled={!isEditing}>
                      <SelectTrigger className="mt-2">
                        <SelectValue placeholder="Select time horizon" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="short-term">Short-term (1-3 years)</SelectItem>
                        <SelectItem value="medium-term">Medium-term (3-7 years)</SelectItem>
                        <SelectItem value="long-term">Long-term (7+ years)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="monthlyInvestment">Monthly Investment</Label>
                    <Select value={profileData.monthlyInvestment} onValueChange={(value) => setProfileData({...profileData, monthlyInvestment: value})} disabled={!isEditing}>
                      <SelectTrigger className="mt-2">
                        <SelectValue placeholder="Select investment amount" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0-5K">₹0 - ₹5,000</SelectItem>
                        <SelectItem value="5K-15K">₹5,000 - ₹15,000</SelectItem>
                        <SelectItem value="15K-50K">₹15,000 - ₹50,000</SelectItem>
                        <SelectItem value="50K-1L">₹50,000 - ₹1 Lakh</SelectItem>
                        <SelectItem value="1L+">₹1+ Lakh</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="preferredSectors">Preferred Sectors</Label>
                    <Input
                      id="preferredSectors"
                      value={profileData.preferredSectors}
                      onChange={(e) => setProfileData({...profileData, preferredSectors: e.target.value})}
                      disabled={!isEditing}
                      className="mt-2"
                      placeholder="e.g., IT, Banking, Pharma, Auto"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Notification Settings */}
        {/* 
        {activeTab === 'notifications' && (
          <Card>
            <CardHeader>
              <CardTitle className="text-2xl">Notification Preferences</CardTitle>
              <p className="text-gray-600">Choose how and when you want to be notified</p>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {Object.entries(notificationSettings).map(([key, value]) => (
                  <div key={key} className="flex items-center justify-between p-4 border rounded-lg">
                    <div>
                      <Label className="font-medium capitalize">
                        {key.replace(/([A-Z])/g, ' $1').trim()}
                      </Label>
                      <p className="text-sm text-gray-500 mt-1">
                        {key === 'emailNotifications' && 'Receive notifications via email'}
                        {key === 'priceAlerts' && 'Get alerts when stock prices change significantly'}
                        {key === 'portfolioUpdates' && 'Daily portfolio performance updates'}
                        {key === 'marketNews' && 'Latest market news and insights'}
                        {key === 'weeklyReports' && 'Weekly portfolio summary reports'}
                        {key === 'securityAlerts' && 'Security and login notifications'}
                      </p>
                    </div>
                    <Switch
                      checked={value}
                      onCheckedChange={() => handleNotificationToggle(key)}
                    />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
        */}

        {/* Security Settings */}
        {activeTab === 'security' && (
          <Card>
            <CardHeader>
              <CardTitle className="text-2xl">Security Settings</CardTitle>
              <p className="text-gray-600">Manage your account security and privacy</p>
              <div className="flex justify-end mt-4">
                <Button 
                  onClick={handleSecuritySettingsSave}
                  className="bg-blue-600 hover:bg-blue-700"
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Settings
                    </>
                  )}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Password Change Section */}
              <div className="border rounded-lg p-6 bg-gray-50">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Change Password</h3>
                    <p className="text-sm text-gray-600">Update your password with OTP verification</p>
                  </div>
                  <Button 
                    onClick={() => setShowPasswordChange(true)}
                    variant="outline"
                    className="bg-white hover:bg-gray-50"
                  >
                    Change Password
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="sessionTimeout">Session Timeout</Label>
                  <Select 
                    value={securitySettings.sessionTimeout} 
                    onValueChange={(value) => {
                      console.log(`🔄 [SECURITY] Session timeout changed to: ${value}`);
                      setSecuritySettings({...securitySettings, sessionTimeout: value});
                    }}
                  >
                    <SelectTrigger className="mt-2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1h">1 Hour</SelectItem>
                      <SelectItem value="8h">8 Hours</SelectItem>
                      <SelectItem value="24h">24 Hours</SelectItem>
                      <SelectItem value="7d">7 Days</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="passwordExpiry">Password Expiry</Label>
                  <Select 
                    value={securitySettings.passwordExpiry} 
                    onValueChange={(value) => {
                      console.log(`🔄 [SECURITY] Password expiry changed to: ${value}`);
                      setSecuritySettings({...securitySettings, passwordExpiry: value});
                    }}
                  >
                    <SelectTrigger className="mt-2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="30d">30 Days</SelectItem>
                      <SelectItem value="60d">60 Days</SelectItem>
                      <SelectItem value="90d">90 Days</SelectItem>
                      <SelectItem value="never">Never</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="loginNotifications">Login Notifications</Label>
                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div>
                      <Label className="font-medium">Login Notifications</Label>
                      <p className="text-sm text-gray-500 mt-1">Get notified of new login attempts via email</p>
                    </div>
                    <Switch
                      checked={securitySettings.loginNotifications}
                      onCheckedChange={() => handleSecurityToggle('loginNotifications')}
                    />
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t">
                <Button variant="destructive" className="bg-red-600 hover:bg-red-700">
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Account
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Preferences */}
        {activeTab === 'preferences' && (
          <Card>
            <CardHeader>
              <CardTitle className="text-2xl">App Preferences</CardTitle>
              <p className="text-gray-600">Customize your StockKhata experience</p>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="theme">Theme</Label>
                  <Select value={preferences.theme} onValueChange={(value) => setPreferences({...preferences, theme: value})}>
                    <SelectTrigger className="mt-2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="light">Light</SelectItem>
                      <SelectItem value="dark">Dark</SelectItem>
                      <SelectItem value="auto">Auto (System)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="language">Language</Label>
                  <Select value={preferences.language} onValueChange={(value) => setPreferences({...preferences, language: value})}>
                    <SelectTrigger className="mt-2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="en">English</SelectItem>
                      <SelectItem value="hi">Hindi</SelectItem>
                      <SelectItem value="gu">Gujarati</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="currency">Currency</Label>
                  <Select value={preferences.currency} onValueChange={(value) => setPreferences({...preferences, currency: value})}>
                    <SelectTrigger className="mt-2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="INR">Indian Rupee (₹)</SelectItem>
                      <SelectItem value="USD">US Dollar ($)</SelectItem>
                      <SelectItem value="EUR">Euro (€)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="timezone">Timezone</Label>
                  <Select value={preferences.timezone} onValueChange={(value) => setPreferences({...preferences, timezone: value})}>
                    <SelectTrigger className="mt-2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Asia/Kolkata">India (IST)</SelectItem>
                      <SelectItem value="America/New_York">New York (EST)</SelectItem>
                      <SelectItem value="Europe/London">London (GMT)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="pt-4 border-t">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium">Data Export</h3>
                    <p className="text-sm text-gray-500">Download your portfolio data</p>
                  </div>
                  <Button variant="outline">
                    <Download className="w-4 h-4 mr-2" />
                    Export Data
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Password Change Modal */}
      {showPasswordChange && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className="relative w-full max-w-md bg-white rounded-lg shadow-2xl p-6">
            <button 
              onClick={clearPasswordChangeModal}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X size={20} />
            </button>
            
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Change Password
              </h2>
              <p className="text-gray-600">
                Enter your current password and choose a new one
              </p>
            </div>

            {passwordChangeError && (
              <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-600 text-sm text-center mb-6">
                <div className="flex items-center justify-center space-x-2">
                  <AlertCircle size={16} />
                  <span>{passwordChangeError}</span>
                </div>
              </div>
            )}

            {passwordChangeMessage && (
              <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg text-green-600 text-sm text-center mb-6">
                {passwordChangeMessage}
              </div>
            )}

            <form onSubmit={handlePasswordChange} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="currentPassword">Current Password</Label>
                <div className="relative">
                  <Input
                    id="currentPassword"
                    type={showCurrentPassword ? "text" : "password"}
                    value={passwordChangeData.currentPassword}
                    onChange={(e) => setPasswordChangeData({...passwordChangeData, currentPassword: e.target.value})}
                    className="pr-10"
                    placeholder="Enter current password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    {showCurrentPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="newPassword">New Password</Label>
                <div className="relative">
                  <Input
                    id="newPassword"
                    type={showNewPassword ? "text" : "password"}
                    value={passwordChangeData.newPassword}
                    onChange={(e) => {
                      setPasswordChangeData({...passwordChangeData, newPassword: e.target.value});
                      checkPasswordStrength(e.target.value);
                    }}
                    className="pr-10"
                    placeholder="Enter new password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    {showNewPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                
                {/* Password Strength Requirements */}
                {passwordChangeData.newPassword && (
                  <div className="mt-3 p-3 bg-gray-50 border border-gray-200 rounded-lg space-y-2">
                    <p className="text-xs text-gray-600 font-medium mb-2">Password must contain:</p>
                    <PasswordRequirement met={passwordStrength.length} text="At least 8 characters" />
                    <PasswordRequirement met={passwordStrength.uppercase} text="One uppercase letter (A-Z)" />
                    <PasswordRequirement met={passwordStrength.lowercase} text="One lowercase letter (a-z)" />
                    <PasswordRequirement met={passwordStrength.number} text="One number (0-9)" />
                    <PasswordRequirement met={passwordStrength.symbol} text="One special character (!@#$%^&*)" />
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm New Password</Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    value={passwordChangeData.confirmPassword}
                    onChange={(e) => setPasswordChangeData({...passwordChangeData, confirmPassword: e.target.value})}
                    className={`pr-10 ${
                      passwordChangeData.confirmPassword 
                        ? (passwordsMatch() ? 'border-green-500 focus:border-green-500' : 'border-red-500 focus:border-red-500')
                        : ''
                    }`}
                    placeholder="Confirm new password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                
                {/* Password Match Indicator */}
                {passwordChangeData.confirmPassword && (
                  <div className={`flex items-center space-x-2 text-sm ${
                    passwordsMatch() ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {passwordsMatch() ? (
                      <Check size={16} />
                    ) : (
                      <AlertCircle size={16} />
                    )}
                    <span>
                      {passwordsMatch() ? 'Passwords match' : 'Passwords do not match'}
                    </span>
                  </div>
                )}
              </div>

              <Button
                type="submit"
                disabled={passwordChangeLoading || !isPasswordStrong() || !passwordsMatch()}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 transform hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                {passwordChangeLoading ? (
                  <div className="flex items-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    <span>Verifying...</span>
                  </div>
                ) : (
                  'Verify & Send OTP'
                )}
              </Button>
            </form>
          </div>
        </div>
      )}

      {/* Password Change OTP Modal */}
      {showPasswordOtpModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className="relative w-full max-w-md bg-white rounded-lg shadow-2xl p-6">
            <button
              onClick={() => setShowPasswordOtpModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X size={20} />
            </button>

            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Verify OTP
              </h2>
              <p className="text-gray-600">
                Enter the 6-digit code sent to your email
              </p>
            </div>

            {passwordChangeError && (
              <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-600 text-sm text-center mb-6">
                <div className="flex items-center justify-center space-x-2">
                  <AlertCircle size={16} />
                  <span>{passwordChangeError}</span>
                </div>
              </div>
            )}

            {passwordChangeMessage && (
              <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg text-green-600 text-sm text-center mb-6">
                {passwordChangeMessage}
              </div>
            )}

            {!passwordOtpVerified ? (
              <div className="space-y-6">
                <div className="grid grid-cols-6 gap-3">
                  {passwordOtp.map((data, index) => (
                    <Input
                      key={index}
                      type="text"
                      maxLength={1}
                      value={data}
                      onChange={e => handlePasswordOtpChange(e.target, index)}
                      onFocus={e => e.target.select()}
                      className="text-center text-lg font-bold border-gray-300 focus:border-blue-500 focus:ring-blue-500/20 transition-colors"
                    />
                  ))}
                </div>

                <Button
                  onClick={handleVerifyPasswordOtp}
                  disabled={passwordOtpLoading || passwordOtp.join("").length !== 6}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 transform hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {passwordOtpLoading ? (
                    <div className="flex items-center space-x-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      <span>Verifying...</span>
                    </div>
                  ) : (
                    'Verify & Change Password'
                  )}
                </Button>
              </div>
            ) : (
              <div className="text-center space-y-4 py-6">
                <div className="mx-auto w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center">
                  <Check size={32} className="text-green-500" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">Password Changed Successfully!</h2>
                <p className="text-gray-600">Your password has been updated.</p>
                <p className="text-gray-600">Redirecting you back...</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
